import { Grid, Link as MuiLink } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import TourSettingItem from './TourSettingItem';
import {
  AccessibleForwardOutlined,
  DirectionsWalkOutlined,
  EmojiSymbolsOutlined,
  EuroOutlined,
  EventOutlined,
  FormatListNumberedOutlined,
  LocationOnOutlined,
  NoMealsOutlined,
  NotAccessibleOutlined,
  PermDeviceInformationOutlined,
  RestaurantOutlined,
  SupervisorAccountOutlined,
  TimerOutlined,
  TranslateOutlined,
} from '@mui/icons-material';
import { DistanceIcon, FlagIcon, LialoIcon, SeasonIcon, TextToSpeechIcon } from '../../../../common/Icons';
import GoogleMapsLink from '../../../../common/Button/GoogleMapsLink';
import { AvailableTranslation, ISimpleLocation, ITourSettings } from '@lialo/core/lib/models';
import { isFalsy } from '@lialo/core/lib/utils/helper/utilities';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '@lialo/common/lib/formatter/money';
import { LanguageSelect } from './LanguageSelect';
import { Language } from '@lialo/common/lib/language';

const PREFIX = 'TourSettingSection';

const classes = {
  container: `${PREFIX}-container`,
  link: `${PREFIX}-link`,
};

const StyledGrid = styled(Grid)(() => ({
  [`&.${classes.container}`]: {
    flexShrink: 0,
    padding: 0,
    margin: 0,
  },

  [`& .${classes.link}`]: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
})) as typeof Grid;

interface IProps {
  tourCode: string;
  translations: AvailableTranslation[];
  language: Language;
  settings?: ITourSettings;
  stops: number;
  tasks: number;
  startLocation?: ISimpleLocation;
  endLocation?: ISimpleLocation;
  price?: string;
}

const TourSettingSection = (props: IProps) => {
  const { t } = useTranslation();

  const isValidSetting = (setting: any) => !isFalsy(setting);
  const translateTags = (section: string, tags: string[] | string) =>
    (Array.isArray(tags) ? tags : [tags]).map(tag => t(`tour_detail.settings.tag_labels.${section}.${tag}`)).join(', ');

  return (
    <StyledGrid container component={'ul'} className={classes.container}>
      <TourSettingItem
        colSpan={12}
        // Feature: Text-to-speech
        icon={props.settings ? TextToSpeechIcon : TranslateOutlined}
        primaryText={t('tour_detail.settings.sections.language')}
        secondaryText={<LanguageSelect tourCode={props.tourCode} availableTranslations={props.translations} />}
      />

      {props.settings && (
        <TourSettingItem
          colSpan={6}
          icon={EuroOutlined}
          primaryText={t('tour_detail.settings.sections.price')}
          secondaryText={props.price ? formatMoney(props.price) : t('common.price_tag.free_of_charge')}
        />
      )}
      {isValidSetting(props.settings?.audience) && (
        <TourSettingItem
          colSpan={6}
          icon={SupervisorAccountOutlined}
          primaryText={t('tour_detail.settings.sections.audience')}
          secondaryText={translateTags('audience', props.settings!.audience!)}
        />
      )}
      {/* "Ort" is currently not displayed in favor of price display
        isValidSetting(props.settings?.environments) && (
        <TourSettingItem
          colSpan={6}
          icon={HouseOutlined}
          primaryText={t('tour_detail.settings.sections.environments')}
          secondaryText={translateTags('environments', props.settings!.environments)}
        />
      )
      */}
      {isValidSetting(props.settings?.duration) && (
        <TourSettingItem
          colSpan={6}
          icon={TimerOutlined}
          primaryText={t('tour_detail.settings.sections.duration')}
          secondaryText={translateTags('duration', props.settings!.duration!!)}
        />
      )}
      {!props.settings?.playAtHome && isValidSetting(props.settings?.distance) && (
        <TourSettingItem
          colSpan={6}
          icon={DistanceIcon}
          primaryText={t('tour_detail.settings.sections.distance')}
          secondaryText={translateTags('distance', props.settings!.distance!!)}
        />
      )}
      {!props.settings?.playAtHome && (
        <>
          <TourSettingItem
            colSpan={6}
            icon={LocationOnOutlined}
            primaryText={t('tour_detail.settings.sections.start_location')}
            secondaryText={
              props.startLocation && (
                <>
                  <GoogleMapsLink
                    button={
                      <MuiLink component={'span'} className={classes.link}>
                        {t('tour_detail.settings.tag_labels.start_location.map')}
                      </MuiLink>
                    }
                    mode={'location'}
                    location={props.startLocation}
                    dialogText={t('tour_detail.settings.tag_labels.start_location.dialog_description')}
                  />
                </>
              )
            }
          />
          <TourSettingItem
            colSpan={6}
            icon={FlagIcon}
            primaryText={t('tour_detail.settings.sections.end_location')}
            secondaryText={
              props.endLocation ? (
                <>
                  <GoogleMapsLink
                    button={
                      <MuiLink component={'span'} className={classes.link}>
                        {t('tour_detail.settings.tag_labels.end_location.one_way')}
                      </MuiLink>
                    }
                    mode={'location'}
                    location={props.endLocation}
                    dialogText={t('tour_detail.settings.tag_labels.end_location.dialog_description')}
                  />
                </>
              ) : (
                t('tour_detail.settings.tag_labels.end_location.round_trip')
              )
            }
          />
        </>
      )}
      {props.stops !== undefined && (
        <TourSettingItem
          colSpan={6}
          icon={LialoIcon}
          primaryText={t('tour_detail.settings.sections.stops')}
          secondaryText={props.stops}
        />
      )}
      {props.tasks !== undefined && (
        <TourSettingItem
          colSpan={6}
          icon={FormatListNumberedOutlined}
          primaryText={t('tour_detail.settings.sections.tasks')}
          secondaryText={props.tasks}
        />
      )}
      {isValidSetting(props.settings?.season) && props.settings!.season !== 'YEAR_LONG' && (
        <TourSettingItem
          colSpan={6}
          icon={SeasonIcon}
          primaryText={t('tour_detail.settings.sections.season')}
          secondaryText={translateTags('season', props.settings!.season!)}
        />
      )}
      {(isValidSetting(props.settings?.occasions) || isValidSetting(props.settings?.playAtHome)) && (
        <TourSettingItem
          colSpan={12}
          icon={EventOutlined}
          primaryText={t('tour_detail.settings.sections.occasions')}
          secondaryText={translateTags(
            'occasions',
            props.settings!.playAtHome ? [...props.settings!.occasions, 'STAY_HOME'] : props.settings!.occasions
          )}
        />
      )}
      {isValidSetting(props.settings?.categories) && (
        <TourSettingItem
          colSpan={12}
          icon={EmojiSymbolsOutlined}
          primaryText={t('tour_detail.settings.sections.categories')}
          secondaryText={translateTags('categories', props.settings!.categories)}
        />
      )}
      {isValidSetting(props.settings?.contentTypes) && (
        <TourSettingItem
          colSpan={12}
          icon={PermDeviceInformationOutlined}
          primaryText={t('tour_detail.settings.sections.content_types')}
          secondaryText={translateTags('content_types', props.settings!.contentTypes)}
        />
      )}

      {!props.settings?.playAtHome && isValidSetting(props.settings?.meansOfTransport) && (
        <TourSettingItem
          colSpan={12}
          icon={DirectionsWalkOutlined}
          primaryText={t('tour_detail.settings.sections.means_of_transport')}
          secondaryText={translateTags('means_of_transport', props.settings!.meansOfTransport!)}
        />
      )}
      {props.settings && !props.settings.playAtHome && (
        <>
          <TourSettingItem
            colSpan={12}
            icon={props.settings.handicappedAccessible ? AccessibleForwardOutlined : NotAccessibleOutlined}
            primaryText={t('tour_detail.settings.sections.handicapped_accessible')}
            secondaryText={
              props.settings.handicappedAccessible
                ? t('tour_detail.settings.tag_labels.handicapped_accessible')
                : t('tour_detail.settings.tag_labels.handicapped_inaccessible')
            }
          />
          <TourSettingItem
            colSpan={12}
            icon={props.settings.shoppingFacilities ? RestaurantOutlined : NoMealsOutlined}
            primaryText={t('tour_detail.settings.sections.shopping_facilities')}
            secondaryText={
              props.settings.shoppingFacilities
                ? t('tour_detail.settings.tag_labels.shopping_facilities')
                : t('tour_detail.settings.tag_labels.no_shopping_facilities')
            }
          />
        </>
      )}
    </StyledGrid>
  );
};

export default TourSettingSection;
